/**
 * SprintFit / ThermaCube — science hub copy & article bodies.
 * Remote images: verified Unsplash + Pexels URLs (invalid Unsplash IDs return 404 and break the whole grid).
 */

export const SCIENCE_HERO = {
  title: 'Recovery science',
  dek: 'Plain-language guides to DOMS, cryotherapy, contrast, sleep, compression, and how muscle actually responds—so you can train hard without guessing the basics.',
  eyebrow: 'SprintFit journal',
};

/** Short credibility strip — educational, not medical claims. */
export const SCIENCE_FACTS = [
  {
    label: 'Contrast',
    stat: 'Alternating warm & cool exposure',
    detail: 'Used in sports medicine for decades to support comfort and circulation around stiff tissue—protocol and timing matter.',
  },
  {
    label: 'EMS-style stimulation',
    stat: 'Neuromuscular electrical stimulation',
    detail: 'Controlled pulses can help wake lazy muscle fibres and complement active recovery—different from “ab belts” sold on late-night TV.',
  },
  {
    label: 'Muscle adaptation',
    stat: 'Damage → repair → stronger',
    detail: 'Training creates micro-stress; blood flow, sleep, and smart thermal work shape how quickly you feel ready for the next session.',
  },
  {
    label: 'Cold exposure',
    stat: 'Local ice to whole-body cryo',
    detail: 'Cooling can blunt pain and swelling signals; protocol, duration, and safety differ wildly between a gel pack and a cryo chamber.',
  },
  {
    label: 'Sleep & hormones',
    stat: 'Repair mostly happens offline',
    detail: 'Deep sleep supports tissue remodelling and learning; no gadget replaces consistent hours and predictable wind-down.',
  },
];

export const SCIENCE_ARTICLES = [
  {
    slug: 'contrast-therapy-explained',
    title: 'Contrast therapy, explained',
    dek: 'What “hot–cold” really means, why athletes use it, and how to think about timing without the bro-science.',
    category: 'Protocol',
    readTime: '9 min',
    publishedAt: '2026-03-12',
    heroImage:
      'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Athlete recovery — contrast and training context',
    blocks: [
      {
        type: 'p',
        text: 'Contrast therapy usually means deliberately switching between warming and cooling tissue—not leaving an ice pack on until you cannot feel your knee, and not guessing whether a towel is “hot enough.” The goal is to use temperature as a signal: warmth tends to increase blood flow and ease stiffness; cool tends to calm acute irritation and blunt pain signals. Alternating the two is where the “contrast” name comes from.',
      },
      {
        type: 'callout',
        title: 'Not medical advice',
        text: 'This article is educational. If you have an injury, circulatory condition, neuropathy, or are pregnant, talk to a clinician before heat or cold therapy.',
      },
      {
        type: 'h2',
        text: 'What is happening in the tissue?',
      },
      {
        type: 'p',
        text: 'Heat encourages vasodilation—blood vessels widen—so more blood can reach working muscle and surrounding connective tissue. That is why a warm pack can feel soothing before gentle movement. Cold does the opposite: vasoconstriction narrows vessels, which can reduce fluid buildup and calm hot, angry tissue right after impact or flare-ups.',
      },
      {
        type: 'p',
        text: 'Contrast proposes a “pumping” idea: warm phase opens circulation, cool phase tightens, repeat. Research in sports settings is mixed but generally agrees on two things—(1) individual response varies a lot, and (2) extremes and sloppy timing create more problems than benefits.',
      },
      {
        type: 'h2',
        text: 'Why a pocket device changes the ritual',
      },
      {
        type: 'p',
        text: 'Traditional contrast is a project: ice trays, towels, timers, and guessing temperatures. A calibrated wearable removes the theatre. You pick a mode, strap on, and let the session end itself—closer to what clinics aim for with controlled windows, without turning your kitchen into a lab accident.',
      },
      {
        type: 'figure',
        src: 'https://images.pexels.com/photos/958960/pexels-photo-958960.jpeg?auto=compress&cs=tinysrgb&w=1600',
        alt: 'Ice tray and cubes — cold pack therapy',
        caption: 'Cold side: ice, gel packs, or cold compresses—short bouts, skin protection, no branded gimmicks required.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1612817288484-6f916006741a?auto=format&fit=crop&w=1600&q=80',
        alt: 'Hot water bottle on fabric — heat pack comfort',
        caption: 'Warm side: heat packs, bottles, or microwavable pads ease stiffness—muscle still adapts through sleep and food.',
      },
      {
        type: 'ul',
        items: [
          'Start with manufacturer guidelines and shorter sessions until you know how you respond.',
          'Avoid falling asleep on heat; check skin every few minutes the first times.',
          'Contrast is a complement to sleep, nutrition, and load management—not a replacement.',
        ],
      },
    ],
    relatedSlugs: ['cryotherapy-and-cold-therapy', 'heat-or-ice-decision-guide', 'doms-deep-dive'],
  },
  {
    slug: 'ems-electrical-muscle-stimulation',
    title: 'What is EMS?',
    dek: 'Electrical muscle stimulation in plain English—how it differs from TENS, what it can and cannot do, and why serious wraps use programmes instead of random buzzing.',
    category: 'Deep dive',
    readTime: '10 min',
    publishedAt: '2026-03-08',
    heroImage:
      'https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Clinical technology and precision',
    blocks: [
      {
        type: 'p',
        text: 'EMS (electrical muscle stimulation) uses electrodes on the skin to send controlled pulses that make motor nerves contract muscle. It is not magic: the muscle still fatigues, still needs fuel, and still adapts through recovery. What changes is that you can recruit fibres in a targeted area when movement is painful, limited, or when you want extra metabolic “wake-up” after long sitting.',
      },
      {
        type: 'h2',
        text: 'EMS vs TENS — cousins, not twins',
      },
      {
        type: 'p',
        text: 'TENS (transcutaneous electrical nerve stimulation) is often tuned to confuse pain signalling—comfortable tingling, gate-control theory, that lane. EMS programmes usually use stronger, wider pulses meant to produce visible muscle contraction. Many consumer devices blur the line; serious recovery hardware separates protocols so you are not guessing.',
      },
      {
        type: 'callout',
        title: 'Reality check',
        text: 'No wearable replaces physiotherapy when you need diagnosis or rehab progression. EMS can be a bridge—not the whole bridge.',
      },
      {
        type: 'h2',
        text: 'Why programmes beat “max buzz”',
      },
      {
        type: 'p',
        text: 'Cheap gadgets sell one knob. Clinical-style devices ramp pulse width, frequency, and rest periods the way a coach would periodise effort: warm-up contractions, endurance blocks, short recovery waves. That is why knee and shoulder wraps from SprintFit ship with named modes instead of a single shock dial.',
      },
      {
        type: 'figure',
        src: '/lifestyle-go-hands-free.jpg',
        alt: 'Hands-free recovery wrap on the leg',
        caption: 'Voluntary training plus targeted stimulation can share the same goal: resilient muscle.',
      },
      {
        type: 'ul',
        items: [
          'Do not use EMS over broken skin, active DVT risk, or pacemakers without medical clearance.',
          'Hydrate; dry skin increases sting and uneven current.',
          'If contraction feels joint-y or sharp, stop and reassess pad placement.',
        ],
      },
    ],
    relatedSlugs: ['compression-recovery-science', 'contrast-therapy-explained', 'muscle-soreness-101'],
  },
  {
    slug: 'muscle-soreness-101',
    title: 'Why muscles ache after training',
    dek: 'DOMS, inflammation, and the 48-hour window—what is normal, what is not, and how heat and cold fit in.',
    category: 'Blog',
    readTime: '7 min',
    publishedAt: '2026-03-01',
    heroImage:
      'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Barbell and strength training',
    blocks: [
      {
        type: 'p',
        text: 'Delayed-onset muscle soreness (DOMS) usually peaks one to two days after unfamiliar or heavy eccentric work—think downhill running or slow lowering phases. Micro-tears and inflammation are part of the story, but so is swelling in connective tissue and nervous-system sensitivity. Mild soreness is common; sharp, local pain with swelling or instability is a different conversation—get evaluated.',
      },
      {
        type: 'h2',
        text: 'The repair timeline',
      },
      {
        type: 'p',
        text: 'Satellite cells patch myofibrils; blood flow delivers amino acids and clears waste. Sleep and protein set the ceiling; passive tools nudge how stiff or calm tissue feels while that process runs. Heat often feels better when stiffness dominates; cold can take the edge off when everything feels “hot” and throbbing.',
      },
      {
        type: 'figure',
        src: '/lifestyle-dual-use.jpg',
        alt: 'Athlete using recovery hardware after training',
        caption: 'Light movement beats total bed rest for most DOMS—pair with sleep, not gadgets alone.',
      },
      {
        type: 'callout',
        title: 'Red flags',
        text: 'One-sided calf pain after travel, numbness, fever, or pain that worsens instead of trending better—seek care.',
      },
      {
        type: 'p',
        text: 'ThermaCube-style contrast sits in the “supportive ritual” bucket: predictable temperatures, hands-free, repeatable. It will not erase a bad programme or under-eating—but it can make the days between hard sessions feel less chaotic.',
      },
    ],
    relatedSlugs: ['doms-deep-dive', 'inflammation-training-response', 'heat-or-ice-decision-guide'],
  },
  {
    slug: 'heat-or-ice-decision-guide',
    title: 'Heat or ice? A practical decision guide',
    dek: 'A flow you can actually remember on Sunday evening when your knee is complaining.',
    category: 'Guide',
    readTime: '5 min',
    publishedAt: '2026-02-22',
    heroImage:
      'https://images.pexels.com/photos/958960/pexels-photo-958960.jpeg?auto=compress&cs=tinysrgb&w=1920',
    heroImageAlt: 'Ice tray — cold therapy at home',
    blocks: [
      {
        type: 'p',
        text: 'Think in layers: acute injury (first 24–72 hours, hot swelling, possible fracture) leans toward cold in short bouts and medical evaluation if weight-bearing hurts. Chronic tightness before movement often likes gentle heat. When both sensations show up—morning stiffness that loosens but flares after work—contrast can be a structured compromise.',
      },
      {
        type: 'figure',
        src: 'https://images.pexels.com/photos/143133/pexels-photo-143133.jpeg?auto=compress&cs=tinysrgb&w=1600',
        alt: 'Ice cubes — cold therapy',
        caption: 'Cold calms acute heat and swelling; use a barrier on skin and keep bouts short at first.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1612817288484-6f916006741a?auto=format&fit=crop&w=1600&q=80',
        alt: 'Hot water bottle on a soft surface — heat therapy',
        caption: 'Gentle heat eases stiffness before movement; check temperature to avoid burns.',
      },
      {
        type: 'h2',
        text: 'Quick rules of thumb',
      },
      {
        type: 'ul',
        items: [
          'Sharp + sudden + swelling → prioritize assessment; cold may soothe while you arrange care.',
          'Dull + stiff + “I need to warm up” → heat or light cardio before static stretching.',
          'Post-workout “everything burns” → short cold or contrast if you tolerate it; sleep still wins.',
        ],
      },
      {
        type: 'callout',
        title: 'One device, three modes',
        text: 'ThermaCube is built so you are not maintaining three rituals—heat-only evenings, ice-only flares, contrast when your physio suggests cycles.',
      },
      {
        type: 'p',
        text: 'Write down what you tried and how you felt the next morning. Two weeks of notes beat one heroic session.',
      },
    ],
    relatedSlugs: ['cryotherapy-and-cold-therapy', 'contrast-therapy-explained', 'doms-deep-dive'],
  },
  {
    slug: 'doms-deep-dive',
    title: 'DOMS in depth: timing, mechanisms, and what actually helps',
    dek: 'Why soreness peaks at 48 hours, what eccentric loading has to do with it, and how heat, cold, and movement fit—without miracle claims.',
    category: 'Deep dive',
    readTime: '14 min',
    publishedAt: '2026-03-20',
    heroImage:
      'https://images.unsplash.com/photo-1574680096145-d05b474e2155?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Athlete training — legs and recovery context',
    blocks: [
      {
        type: 'p',
        text: 'Delayed-onset muscle soreness (DOMS) is the stiff, tender feeling that often shows up 12–24 hours after a hard session and peaks around 24–72 hours. It is most common after unfamiliar volume, heavy eccentric work (lowering slowly under load), or long downhill running. DOMS is uncomfortable but usually benign; sharp, focal pain with immediate swelling, numbness, or inability to bear weight is not “just DOMS”—get assessed.',
      },
      {
        type: 'callout',
        title: 'Not medical advice',
        text: 'This guide is educational. If pain is severe, one-sided, or follows trauma, see a clinician. Diabetes, kidney disease, and some medications change how you should use heat or cold.',
      },
      {
        type: 'h2',
        text: 'What is going on under the skin?',
      },
      {
        type: 'p',
        text: 'The old story was “lactic acid”—that is not the main driver of next-day soreness. DOMS lines up more with micro-disruption in muscle and connective tissue, local inflammation, and increased sensitivity of pain receptors. Satellite cells and immune signalling participate in repair; the process is normal adaptation, not a sign you “destroyed” muscle.',
      },
      {
        type: 'h3',
        text: 'Why eccentrics sting more',
      },
      {
        type: 'p',
        text: 'During lengthening contractions, fewer motor units share the load per fibre, so mechanical tension per unit can spike. That pattern predicts more DOMS than concentric-only work at similar perceived effort. This is why your first week of negatives, plyometrics, or a new sport can feel brutal even if you are “fit.”',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=1600&q=80',
        alt: 'Gym floor — loaded training and eccentric work',
        caption: 'Novel loading patterns—not only “heavy weight”—are a classic DOMS trigger.',
      },
      {
        type: 'h2',
        text: 'What helps (and what is oversold)',
      },
      {
        type: 'p',
        text: 'Nothing erases DOMS instantly. Evidence for any single modality is messy because studies use different protocols and people respond differently. Practical levers that consistently matter: sleep, adequate protein spread across the day, gradual progression, and light movement (walking, easy cycling) to reduce stiffness.',
      },
      {
        type: 'ul',
        items: [
          'Heat or contrast may improve comfort and perceived readiness when stiffness dominates—watch skin integrity and session length.',
          'Short cold bouts can blunt soreness perception for some people; they do not “speed protein synthesis” by magic.',
          'Foam rolling and easy mobility often change how soreness feels within a session, partly via nervous-system effects.',
          'Anti-inflammatory drugs can reduce discomfort; chronic blanket use around training is a nuanced topic—ask your clinician if you rely on them.',
        ],
      },
      {
        type: 'h2',
        text: 'Programming takeaways',
      },
      {
        type: 'p',
        text: 'Respect the 48-hour window after a spike in novelty: you can train, but repeating maximal eccentric volume on the same tissue may dig a hole. Alternate emphasis, keep one day easier, or shift to technique work while soreness trends down. Tracking soreness 1–10 plus session RPE helps you spot when you are under-recovering before injury risk rises.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1538805060514-97d9cc17730c?auto=format&fit=crop&w=1600&q=80',
        alt: 'Runner stretching — mobility between hard training days',
        caption: 'Easy movement usually beats total stillness for DOMS—pair it with sleep and food.',
      },
    ],
    relatedSlugs: ['muscle-soreness-101', 'inflammation-training-response', 'foam-rolling-self-myofascial-release'],
  },
  {
    slug: 'cryotherapy-and-cold-therapy',
    title: 'Cryotherapy & cold therapy: ice packs to cryo chambers',
    dek: 'How local cooling, ice baths, and whole-body cryo differ; what the evidence suggests; and how to stay safe.',
    category: 'Deep dive',
    readTime: '13 min',
    publishedAt: '2026-03-18',
    heroImage:
      'https://images.unsplash.com/photo-1517649763962-0c623066013b?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Winter sport — cold exposure context',
    blocks: [
      {
        type: 'p',
        text: '“Cryotherapy” gets used for everything from a bag of peas on a knee to a −110 °C nitrogen chamber. Cold therapy generally aims to lower tissue temperature, blunt acute pain signalling, and reduce blood flow and metabolic demand in the short term. The right tool depends on the goal: local swelling after a tweak is not the same as whole-body recovery after a tournament.',
      },
      {
        type: 'callout',
        title: 'Safety first',
        text: 'Raynaud’s phenomenon, cold urticaria, significant neuropathy, open wounds, or pregnancy are reasons to get clinician guidance before aggressive cold protocols. Never sleep on ice.',
      },
      {
        type: 'h2',
        text: 'Local cold vs whole-body cryotherapy',
      },
      {
        type: 'p',
        text: 'Local cooling—gel packs, cold wraps, targeted baths—changes temperature in a defined area. Whole-body cryo (WBC) exposes the skin to extremely cold dry air for short bursts. Studies conflict on whether WBC improves performance markers more than cheaper methods; many athletes report feeling fresher, which still matters for subjective readiness even when lab differences are small.',
      },
      {
        type: 'h3',
        text: 'Ice baths: what they might do',
      },
      {
        type: 'p',
        text: 'Cold-water immersion after heavy training may reduce perceived soreness for some people. It can also blunt certain acute signalling pathways that participate in adaptation—so coaches sometimes avoid ice immediately after sessions where the priority is maximal hypertrophy response. That nuance is population-specific; rec athletes often prioritize comfort and next-day function over squeezing the last theoretical percent.',
      },
      {
        type: 'figure',
        src: 'https://images.pexels.com/photos/143133/pexels-photo-143133.jpeg?auto=compress&cs=tinysrgb&w=1600',
        alt: 'Ice cubes — abstract cold temperature visual',
        caption: 'Cold is a tool with trade-offs: duration, depth of cooling, and timing relative to training all matter.',
      },
      {
        type: 'h2',
        text: 'Practical protocol ideas',
      },
      {
        type: 'ul',
        items: [
          'Acute knock with heat and swelling: short local cold (10–15 minutes), protect skin with a barrier, elevate if advised.',
          'DOMS comfort: brief cool shower or limb immersion if you tolerate it; pair with sleep and calories.',
          'Contrast cycles: alternate comfortable warm and cool within safe ranges—see our contrast guide for structure.',
          'Hydrate before and after; cold vasoconstriction does not cancel fluid needs.',
        ],
      },
      {
        type: 'p',
        text: 'A pocket thermal device is not a cryo chamber, but calibrated local cooling can still offer repeatable sessions without filling a tub—useful when travel, time, or hygiene makes immersion impractical.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1538805060514-97d9cc17730c?auto=format&fit=crop&w=1600&q=80',
        alt: 'Runner cooling down — post-session recovery',
        caption: 'Subjective recovery and objective markers both matter—choose cold protocols you will actually repeat.',
      },
    ],
    relatedSlugs: ['heat-or-ice-decision-guide', 'contrast-therapy-explained', 'hydration-electrolytes-athletes'],
  },
  {
    slug: 'sleep-and-muscle-repair',
    title: 'Sleep and muscle repair: the non-negotiable layer',
    dek: 'Why stages of sleep matter, how short sleep shows up in training, and habits that cost nothing.',
    category: 'Blog',
    readTime: '12 min',
    publishedAt: '2026-03-15',
    heroImage:
      'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Calm bedroom — sleep environment for recovery',
    blocks: [
      {
        type: 'p',
        text: 'Gadgets and protocols make headlines, but sleep is the backbone of recovery. Growth hormone pulses, tissue remodelling, and motor learning consolidation lean heavily on consistent, sufficient sleep. Cutting sleep by even an hour or two for several nights shows up as higher injury risk, worse mood, and shakier performance in both strength and endurance research.',
      },
      {
        type: 'h2',
        text: 'What sleep is doing for athletes',
      },
      {
        type: 'p',
        text: 'Slow-wave (deep) sleep supports physical restoration; REM sleep supports skill integration and emotional regulation. Fragmented sleep—alcohol late at night, irregular schedules, untreated apnea—steals cycles even if “time in bed” looks fine on your watch.',
      },
      {
        type: 'h3',
        text: 'Signs sleep debt is biting',
      },
      {
        type: 'ul',
        items: [
          'Morning resting heart rate or HRV trends drift for no other clear reason.',
          'Same RPE sessions feel harder; cravings and irritability creep up.',
          'Illness frequency increases; minor niggles take longer to settle.',
        ],
      },
      {
        type: 'figure',
        src: 'https://images.pexels.com/photos/3771069/pexels-photo-3771069.jpeg?auto=compress&cs=tinysrgb&w=1600',
        alt: 'Person resting in bed — deep sleep matters for repair',
        caption: 'Consistency beats “catch-up” weekends—regular bed and wake times train your circadian rhythm.',
      },
      {
        type: 'h2',
        text: 'Habits with high return',
      },
      {
        type: 'p',
        text: 'Dim lights and screens earlier; keep the bedroom cool and dark; caffeine cut-off ~8–10 hours before bed for sensitive people; heavy training late at night raises core temperature—schedule a wind-down if you must lift after work. Naps (20–30 minutes) can buffer a bad night without replacing chronic debt.',
      },
      {
        type: 'callout',
        title: 'Reality check',
        text: 'No contrast session fixes chronic four-hour nights. Stack sleep first; thermal tools are garnish.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=1600&q=80',
        alt: 'Sunrise over hills — morning light for circadian rhythm',
        caption: 'Morning outdoor light anchors circadian timing—cheap recovery leverage.',
      },
    ],
    relatedSlugs: ['doms-deep-dive', 'hydration-electrolytes-athletes', 'muscle-soreness-101'],
  },
  {
    slug: 'compression-recovery-science',
    title: 'Compression for recovery: sleeves, boots, and what they change',
    dek: 'Mechanical pressure, fluid dynamics, and when compression feels worth the squeeze.',
    category: 'Protocol',
    readTime: '11 min',
    publishedAt: '2026-03-14',
    heroImage:
      'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Athletes’ legs on a trail — movement, mileage, and compression context',
    blocks: [
      {
        type: 'p',
        text: 'Compression garments and intermittent pneumatic compression (“recovery boots”) apply external pressure to limbs. Goals include supporting venous return, reducing the sensation of heaviness after long efforts, and sometimes modestly altering limb swelling. Evidence is mixed but generally kinder to travel, standing jobs, and post-endurance soreness than to being a magic bullet for strength gains.',
      },
      {
        type: 'h2',
        text: 'Mechanisms you can actually explain',
      },
      {
        type: 'p',
        text: 'Graduated compression—tighter at the ankle, looser up the calf—aims to assist blood and lymph against gravity. Pneumatic devices cycle pressure in chambers to milk fluid from distal to proximal tissue. Neither replaces muscle contraction (walking is still king), but they can complement when legs are too cooked to move much.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=1600&q=80',
        alt: 'Running track — athlete legs in motion',
        caption: 'Movement plus compression beats passive sitting—order matters after long sessions.',
      },
      {
        type: 'h2',
        text: 'Who tends to notice benefit',
      },
      {
        type: 'ul',
        items: [
          'Endurance athletes after big mileage or flight days.',
          'Shift workers on feet; people with mild orthostatic leg fatigue.',
          'Post-session when you want a ritual that feels “doing something” without more impact load.',
        ],
      },
      {
        type: 'callout',
        title: 'Contraindications',
        text: 'Active DVT, severe peripheral arterial disease, untreated congestive heart failure, or broken skin under pressure zones—avoid until cleared.',
      },
      {
        type: 'p',
        text: 'EMS targets neuromuscular contraction; compression targets pressure gradients. Some athletes stack light movement, compression, and thermal work on different timelines—experiment with what leaves you fresher, not what looks coolest on social media.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=1600&q=80',
        alt: 'Gym equipment — recovery and training tools',
        caption: 'Fit and pressure matter: too loose is decorative; too tight can numb feet.',
      },
    ],
    relatedSlugs: ['ems-electrical-muscle-stimulation', 'contrast-therapy-explained', 'doms-deep-dive'],
  },
  {
    slug: 'inflammation-training-response',
    title: 'Inflammation after training: friend, foe, and context',
    dek: 'Acute inflammation vs chronic low-grade noise—and why “stop all inflammation” is the wrong default.',
    category: 'Deep dive',
    readTime: '12 min',
    publishedAt: '2026-03-11',
    heroImage:
      'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Training and human performance abstract',
    blocks: [
      {
        type: 'p',
        text: 'Inflammation is not a moral failing—it is signalling. After training, immune cells and cytokines help clear debris and coordinate repair. Blunting that response every single day with ice, NSAIDs, and stress can interfere with adaptation; ignoring raging, prolonged inflammation can leave you sore, stiff, and underperforming. The skill is matching the tool to the phase.',
      },
      {
        type: 'h2',
        text: 'Acute vs chronic: different games',
      },
      {
        type: 'p',
        text: 'Acute post-exercise inflammation peaks and resolves over roughly 24–72 hours in healthy people—hello, DOMS window. Chronic low-grade inflammation from poor sleep, illness, obesity, or overtraining shows up as flat energy, nagging tendons, and mood changes. The second pattern needs lifestyle and medical sleuthing, not another ice bath.',
      },
      {
        type: 'h3',
        text: 'NSAIDs and training',
      },
      {
        type: 'p',
        text: 'Occasional ibuprofen for a headache differs from chronic high-dose use around every lift. Some studies suggest routine NSAIDs may modestly damp hypertrophic signalling in untrained people; real-world impact varies. If you live on anti-inflammatories to train, a clinician should review why pain is that loud.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1576678927484-cc907957088c?auto=format&fit=crop&w=1600&q=80',
        alt: 'Medical science abstract — research context',
        caption: 'Cytokines are messengers, not villains—context decides whether blocking them helps or backfires.',
      },
      {
        type: 'h2',
        text: 'Putting it together',
      },
      {
        type: 'ul',
        items: [
          'Respect a flare: protect, offload, use short cold if it calms acute heat and swelling.',
          'Support resolution: sleep, protein, gradual reload, and manageable stress.',
          'Avoid worshipping “zero inflammation”—repair needs the first wave.',
        ],
      },
      {
        type: 'p',
        text: 'Heat and contrast do not “cure” inflammation; they change sensation, blood flow, and your willingness to move. Pair them with the boring pillars and you get durable progress.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1574680096145-d05b474e2155?auto=format&fit=crop&w=1600&q=80',
        alt: 'Athlete training — strength and adaptation',
        caption: 'Training plus recovery timing beats chasing a single lab marker in isolation.',
      },
    ],
    relatedSlugs: ['doms-deep-dive', 'muscle-soreness-101', 'sleep-and-muscle-repair'],
  },
  {
    slug: 'foam-rolling-self-myofascial-release',
    title: 'Foam rolling & self-massage: nerves, not mayonnaise',
    dek: 'What foam rolling probably does, what it does not do to fascia, and simple protocols that feel good.',
    category: 'Guide',
    readTime: '10 min',
    publishedAt: '2026-03-09',
    heroImage:
      'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Mobility and stretching practice',
    blocks: [
      {
        type: 'p',
        text: 'Self-myofascial release (SMR) with rollers, balls, or massage guns is everywhere. The old story—that you are mechanically smoothing fascia like dough—is oversimplified. More plausible mechanisms include changing muscle tone via reflex arcs, improving perceived stiffness, and modestly increasing short-term range of motion without static stretching alone.',
      },
      {
        type: 'h2',
        text: 'What you can expect in the real world',
      },
      {
        type: 'p',
        text: 'Foam rolling for 30–90 seconds per area can temporarily increase joint ROM and reduce soreness ratings for some athletes. Effects are short-lived, which is fine if you use it as prep or wind-down, not as a replacement for strength through full range.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?auto=format&fit=crop&w=1600&q=80',
        alt: 'Yoga mat and exercise — mobility work',
        caption: 'Pressure should be “hurts good,” not white-knuckle pain—protect nerves and bony spots.',
      },
      {
        type: 'h2',
        text: 'Simple protocol',
      },
      {
        type: 'ul',
        items: [
          'Calves, quads, glutes, upper back (avoid rolling directly over spine): slow passes, breathe.',
          'Pair with easy cardio or dynamic warm-up before heavy lifting.',
          'Massage guns: keep on muscle belly, avoid arteries and acute injuries.',
        ],
      },
      {
        type: 'callout',
        title: 'Myth check',
        text: 'Rolling does not “break up scar tissue” in a literal sense minutes before practice. It can still change how movement feels—use honest language.',
      },
      {
        type: 'p',
        text: 'Combine rolling with heat for stubborn stiffness, or with light cold if everything feels hot and throbbing after a summer race—same rules as our heat vs ice guide.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1600&q=80',
        alt: 'Stretching and mobility — movement quality',
        caption: 'SMR is a dial for comfort and readiness—not a substitute for load management.',
      },
    ],
    relatedSlugs: ['doms-deep-dive', 'heat-or-ice-decision-guide', 'contrast-therapy-explained'],
  },
  {
    slug: 'hydration-electrolytes-athletes',
    title: 'Hydration & electrolytes beyond “drink more water”',
    dek: 'Sweat sodium, hypohydration, and practical targets for training in heat—India included.',
    category: 'Blog',
    readTime: '11 min',
    publishedAt: '2026-03-07',
    heroImage:
      'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&w=2000&q=80',
    heroImageAlt: 'Water and hydration — glass of water',
    blocks: [
      {
        type: 'p',
        text: 'Hydration is not only volume—it is fluid plus electrolytes in balance. Heavy sweaters can lose surprising sodium; drinking plain water alone for hours can dilute blood sodium (hyponatremia) in endurance contexts. In hot, humid training, thirst plus urine colour are crude but useful; precision athletes weigh themselves before and after key sessions to estimate sweat rate.',
      },
      {
        type: 'h2',
        text: 'Electrolytes: what matters',
      },
      {
        type: 'p',
        text: 'Sodium dominates sweat salt loss; potassium and magnesium participate in muscle excitability. Commercial mixes vary widely—some are candy-water; others approximate physiology better. Food-first approaches (salted meals, dairy, fruit) work for many gym-goers; long sessions in heat may need engineered drinks.',
      },
      {
        type: 'figure',
        src: 'https://images.pexels.com/photos/143573/pexels-photo-143573.jpeg?auto=compress&cs=tinysrgb&w=1600',
        alt: 'Citrus and fruit — natural electrolyte foods',
        caption: 'Whole foods carry potassium and carbs; sodium often needs intentional adds for salty sweaters.',
      },
      {
        type: 'h2',
        text: 'Signs you are under-hydrated or imbalanced',
      },
      {
        type: 'ul',
        items: [
          'Dizziness, dark minimal urine, cramping that clusters in heat.',
          'Post-session headaches when you drank mostly plain water.',
          'Heart rate drift higher than normal at the same pace or load.',
        ],
      },
      {
        type: 'callout',
        title: 'Medical edge cases',
        text: 'Heart failure, kidney disease, and BP medications change fluid targets—personalize with your doctor.',
      },
      {
        type: 'p',
        text: 'Cold and contrast sessions still sweat; rehydrate after. Travel dehydration stacks with jet lag—pair fluid strategy with the sleep article for multi-day events.',
      },
      {
        type: 'figure',
        src: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=1600&q=80',
        alt: 'Athlete drinking during outdoor training',
        caption: 'Sip on schedule during long efforts; chug mindfully after—your gut has a throughput limit.',
      },
    ],
    relatedSlugs: ['sleep-and-muscle-repair', 'cryotherapy-and-cold-therapy', 'heat-or-ice-decision-guide'],
  },
];

export function getScienceArticleBySlug(slug) {
  return SCIENCE_ARTICLES.find((a) => a.slug === slug) ?? null;
}

export function getRelatedArticles(slug) {
  const article = getScienceArticleBySlug(slug);
  if (!article?.relatedSlugs?.length) return [];
  return article.relatedSlugs
    .map((s) => getScienceArticleBySlug(s))
    .filter(Boolean);
}
